import { useState } from 'react';
import { X, Mail, Check, AlertCircle, BookmarkCheck } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';

export default function SaveProgressModal({ onClose }) {
  const { linkEmail, userEmail } = useSubscription();
  const [email, setEmail] = useState(userEmail || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSave = async () => {
    if (!email.trim() || !email.includes('@')) {
      setResult({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }
    setLoading(true);
    setResult(null);
    const data = await linkEmail(email.trim());
    setLoading(false);
    if (data.success) {
      setResult({
        type: 'success',
        text: data.restored
          ? 'Account restored! Your usage and subscription have been recovered.'
          : 'Email saved! Your progress is now linked to your email.',
      });
      setTimeout(() => onClose(), 1800);
    } else {
      setResult({ type: 'error', text: data.message || 'Something went wrong. Please try again.' });
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end justify-center bg-black/50 backdrop-blur-sm px-0" data-testid="save-progress-modal">
      <div className="bg-white rounded-t-3xl w-full max-w-[430px] shadow-2xl animate-slide-up overflow-hidden">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[#FFE0E8]" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-3 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FFE0E8] to-[#F0E6F8] flex items-center justify-center">
              <BookmarkCheck size={20} color="#F4436C" />
            </div>
            <div>
              <h2 className="text-[#4A2C35] font-bold text-base">Save Your Progress</h2>
              <p className="text-[#B08EA0] text-xs">Never lose your usage across devices</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#FFF0F3] flex items-center justify-center"
            data-testid="save-progress-close"
          >
            <X size={16} color="#8B6B75" />
          </button>
        </div>

        <div className="px-5 pb-6">
          {/* Info box */}
          <div className="bg-[#FFF8F9] rounded-2xl p-3 border border-[#FFE0E8] mb-4">
            <p className="text-[#4A2C35] text-xs leading-relaxed">
              Link your email once — if you ever reinstall the app, just enter your email to restore everything: your free uses, subscription, and progress.
            </p>
          </div>

          {/* Email input */}
          <label className="text-[#4A2C35] text-sm font-semibold mb-2 block">Your Email</label>
          <div className="relative mb-4">
            <Mail size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#B08EA0]" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              placeholder="you@example.com"
              data-testid="save-progress-email-input"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#FFE0E8] bg-[#FFF8F9] text-[#4A2C35] text-sm focus:outline-none focus:border-[#FF8FAF] transition-colors"
            />
          </div>

          {/* Result message */}
          {result && (
            <div className={`flex items-center gap-2 p-3 rounded-xl mb-4 text-sm ${
              result.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
            }`}>
              {result.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
              <span>{result.text}</span>
            </div>
          )}

          {/* CTA */}
          <button
            onClick={handleSave}
            disabled={loading}
            data-testid="save-progress-submit-btn"
            className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-[#FF8FAF] to-[#F4436C] text-white font-bold text-sm shadow-md active:scale-[0.98] transition-all disabled:opacity-70 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : userEmail ? (
              'Update Email'
            ) : (
              'Save & Link Email'
            )}
          </button>
          <p className="text-center text-[10px] text-[#B08EA0] mt-3">
            Works as both progress saver and account restorer
          </p>
        </div>
      </div>
    </div>
  );
}
